import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { safeOpen } from '../utils/safeRedirect';
import { formatPhoneInput, otpErrorMessage, PHONE_FORMAT_ERROR, PHONE_HELP_TEXT, toE164 } from '../utils/phone';
// v1.1 - support chat + auth bridge
import toast, { Toaster } from 'react-hot-toast';
import { sendSupportRequest } from '../design/data/messageShoutOut';
import LeaderboardNameSetting from '../components/LeaderboardNameSetting';

// Creator support lives in the Creator Inbox ("Message ShoutOut"), handled by
// the Creator Success team, not in the fans' Help Desk. `tab=inbox` survives
// the dashboard's sign-in handoff; `filter` applies when already signed in.
const CREATOR_INBOX_URL = 'https://creator.shoutout.us/dashboard?tab=inbox&filter=shoutout';

type Tab = 'cards' | 'orders' | 'account';

interface SupportMessage {
  id: string;
  user_id: string;
  message: string;
  response?: string;
  is_resolved: boolean;
  is_read: boolean;
  created_at: string;
  updated_at: string;
}

interface SavedCard {
  id: number;
  sourceType: string;
  lastDigits: string;
  nameHolder?: string;
  isDefault: boolean;
  expMonth?: string;
  expYear?: string;
}

interface OrderItem {
  id: string;
  occasion: string;
  amount: number;
  status: string;
  created_at: string;
  video_url: string | null;
  is_express_delivery: boolean;
  recipient_name: string | null;
  request_details: string | null;
  talent_id: string;
  talent_name: string;
  talent_image: string | null;
  talent_slug: string;
  is_audio_shoutout?: boolean;
  shoutout_tier?: string;
  service_type?: string;
  details_submitted?: boolean;
  is_scheduled?: boolean;
  scheduled_event_date?: string | null;
  scheduled_charge_date?: string | null;
}

interface UserAccount {
  id: string;
  email: string;
  full_name: string;
  phone: string;
  avatar_url: string | null;
  created_at: string;
  lunarpay_customer_id: number | null;
  credits: number;
}

const AccountPage: React.FC = () => {
  const navigate = useNavigate();

  // Auth state
  const [authStep, setAuthStep] = useState<'email' | 'phone' | 'otp' | 'authenticated'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Account data
  const [user, setUser] = useState<UserAccount | null>(null);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>((searchParams.get('tab') as Tab) || 'orders');
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [accountLoading, setLoading] = useState(false);

  // Order detail state
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [editingSubAmount, setEditingSubAmount] = useState<string>('');
  const [editingSubOrderId, setEditingSubOrderId] = useState<string | null>(null);
  const [subActionLoading, setSubActionLoading] = useState<string | null>(null);

  // Review + download state
  const [reviewOrderId, setReviewOrderId] = useState<string | null>(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Fill in details state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsOrderId, setDetailsOrderId] = useState<string | null>(null);
  const [detailsRecipientName, setDetailsRecipientName] = useState('');
  const [detailsMention1, setDetailsMention1] = useState('');
  const [detailsMention2, setDetailsMention2] = useState('');
  const [submittingDetails, setSubmittingDetails] = useState(false);

  // Support chat state
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [supportMessages, setSupportMessages] = useState<SupportMessage[]>([]);
  const [supportUnread, setSupportUnread] = useState(0);
  const [newSupportMsg, setNewSupportMsg] = useState('');
  // A creator with a claimed page and the Creator Inbox on: their support goes
  // there. null while checking or after a failed check: sending waits (with a
  // retry), so a creator's message can never slip into the fans' Help Desk.
  const [isCreator, setIsCreator] = useState<boolean | null>(null);
  const [creatorCheckFailed, setCreatorCheckFailed] = useState(false);
  const [creatorCheckRun, setCreatorCheckRun] = useState(0);
  const [creatorSupportSent, setCreatorSupportSent] = useState(false);
  const [creatorNeedsDashboard, setCreatorNeedsDashboard] = useState(false);
  const [sendingSupport, setSendingSupport] = useState(false);
  const [loadingSupport, setLoadingSupport] = useState(false);
  const supportEndRef = useRef<HTMLDivElement>(null);

  // Add funds state
  const [fundAmount, setFundAmount] = useState('');
  const [fundCardId, setFundCardId] = useState<number | null>(null);
  const [addingFunds, setAddingFunds] = useState(false);

  // Edit state
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [saving, setSaving] = useState(false);

  // Add card state
  const [addingCard, setAddingCard] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cardInstance, setCommerceInstance] = useState<any>(null);
  const [cardFormReady, setCardFormReady] = useState(false);
  const cardContainerRef = useRef<HTMLDivElement>(null);

  // Check for existing session on mount — auto-login if possible
  useEffect(() => {
    const checkSession = async () => {
      // 1. Check Supabase auth session
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        setEmail(session.user.email);
        localStorage.setItem('bio_subscriber_email', session.user.email);
        localStorage.setItem('bio_account_verified', 'true');
        await loadAccount(session.user.email);
        return;
      }

      // 2. Check if previously verified (skip OTP on refresh)
      const saved = localStorage.getItem('bio_subscriber_email');
      const verified = localStorage.getItem('bio_account_verified');
      if (saved && verified === 'true') {
        setEmail(saved);
        await loadAccount(saved);
        return;
      }

      // 3. Have email but not verified yet — send OTP
      if (saved) {
        setEmail(saved);
        setAuthLoading(true);
        try {
          const { data, error } = await supabase.functions.invoke('send-registration-otp', {
            body: { email: saved.toLowerCase().trim(), checkEmailOnly: true },
          });
          if (error || data?.error || data?.success === false) {
            // The phone on file couldn't be texted: say why, don't show a code step.
            toast.error(await otpErrorMessage(error, data));
            setAuthStep('email');
          } else if (data?.sentToExistingPhone) {
            setPhone(data.phone || '');
            setAuthStep('otp');
          } else {
            setAuthStep('phone');
          }
        } catch (e) {
          setAuthStep('email');
        } finally {
          setAuthLoading(false);
        }
        return;
      }

      // 4. No session, no saved email
      setAuthStep('email');
    };
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAccount = async (emailAddr: string) => {
    setLoading(true);
    try {
      supabase.functions.invoke('lunarpay-api', {
        body: { action: 'ensure-auth-user', email: emailAddr },
      }).catch(() => {});

      const { data, error } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'get-account', email: emailAddr },
      });
      if (!error && data?.user) {
        setUser(data.user);
        setEditName(data.user.full_name || '');
        setEditPhone(data.user.phone || '');
        setAuthStep('authenticated');
        loadCards(emailAddr);
        loadOrders(emailAddr, data.user.id);
      } else {
        setAuthStep('email');
      }
    } catch (e) {
      console.log('Account load error:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadCards = async (emailAddr: string) => {
    try {
      const { data } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'get-payment-methods', email: emailAddr },
      });
      if (data?.paymentMethods) setSavedCards(data.paymentMethods);
    } catch (e) {
      console.log('Cards load error:', e);
    }
  };

  const loadOrders = async (emailAddr: string, userId?: string) => {
    try {
      const { data } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'get-orders', email: emailAddr, userId },
      });
      if (data?.orders) setOrders(data.orders);
    } catch (e) {
      console.log('Orders load error:', e);
    }
  };

  const loadSupportMessages = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'get-support-messages', userId },
      });
      if (error) { console.log('Support fetch error:', error); return; }
      if (data?.messages) setSupportMessages(data.messages);
      if (typeof data?.unreadCount === 'number') setSupportUnread(data.unreadCount);
    } catch (e) {
      console.log('Support load error:', e);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    loadSupportMessages(user.id);
    const interval = setInterval(() => loadSupportMessages(user.id), 30000);
    return () => clearInterval(interval);
  }, [user?.id, loadSupportMessages]);

  useEffect(() => {
    if (showSupportChat) supportEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [supportMessages, showSupportChat]);

  useEffect(() => {
    let cancelled = false;
    setCreatorCheckFailed(false);
    if (!user?.id) {
      setIsCreator(false);
      return;
    }
    setIsCreator(null);
    (async () => {
      const { data, error } = await supabase
        .from('talent_profiles')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);
      if (cancelled) return;
      if (error) {
        setCreatorCheckFailed(true);
        return;
      }
      if ((data?.length ?? 0) === 0) {
        setIsCreator(false);
        return;
      }
      // Same switch check as the dashboard: while the Creator Inbox is off or
      // staff-only, the handoff would refuse, so the Help Desk stays.
      const { data: inboxOn, error: inboxError } = await supabase.rpc('creator_inbox_enabled');
      if (cancelled) return;
      if (inboxError) {
        setCreatorCheckFailed(true);
        return;
      }
      setIsCreator(inboxOn === true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, creatorCheckRun]);

  const sendCreatorSupport = async () => {
    setSendingSupport(true);
    try {
      await sendSupportRequest(newSupportMsg, null);
      setNewSupportMsg('');
      setCreatorSupportSent(true);
      toast.success('Sent. A real person will reply next business day.');
    } catch (e: any) {
      // No signed-in session on this page (signed in by email only): the
      // message can't be tied to the creator here, so send them to the
      // dashboard rather than into the fans' Help Desk.
      if (/not signed in/i.test(e?.message || '')) setCreatorNeedsDashboard(true);
      else toast.error(e?.message || 'Could not send your message. Try again.');
    } finally {
      setSendingSupport(false);
    }
  };

  const sendSupportMessage = async () => {
    if (!newSupportMsg.trim() || sendingSupport || !user?.id || isCreator === null) return;
    if (isCreator) return sendCreatorSupport();
    setSendingSupport(true);
    try {
      const { error } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'send-support-message', userId: user.id, message: newSupportMsg.trim() },
      });
      if (error) throw error;
      setNewSupportMsg('');
      toast.success('Message sent!');
      await loadSupportMessages(user.id);
    } catch (e) {
      toast.error('Failed to send message');
    } finally {
      setSendingSupport(false);
    }
  };

  const openSupportChat = async () => {
    setShowSupportChat(true);
    setLoadingSupport(true);
    if (user?.id) {
      await loadSupportMessages(user.id);
      await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'mark-support-read', userId: user.id },
      });
      setSupportUnread(0);
    }
    setLoadingSupport(false);
  };

  // Auth flow: email -> check if exists -> OTP
  const handleEmailSubmit = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    setAuthLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: email.toLowerCase().trim(), checkEmailOnly: true },
      });
      if (error || data?.error || data?.success === false) {
        toast.error(await otpErrorMessage(error, data));
        return;
      }
      if (data?.sentToExistingPhone) {
        setPhone(data.phone || '');
        setAuthStep('otp');
        toast.success(`Code sent to ${data.phone ? '***' + data.phone.slice(-4) : 'your phone'}`);
      } else {
        setAuthStep('phone');
      }
    } catch (e) {
      toast.error('Failed to check account');
    } finally {
      setAuthLoading(false);
    }
  };

  const handlePhoneSubmit = async () => {
    const e164 = toE164(phone);
    if (!e164) {
      toast.error(PHONE_FORMAT_ERROR);
      return;
    }
    setAuthLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: email.toLowerCase().trim(), phone: e164 },
      });
      if (error || data?.error || data?.success === false) {
        toast.error(await otpErrorMessage(error, data));
        return;
      }
      setAuthStep('otp');
      toast.success('Verification code sent!');
    } catch (e) {
      toast.error('Failed to send code');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleOtpVerify = async () => {
    if (!otpCode || otpCode.length < 4) return;
    setAuthLoading(true);
    try {
      const verifyPhone = phone && !phone.includes('*') ? toE164(phone) || undefined : undefined;
      const { data, error } = await supabase.functions.invoke('verify-registration-otp', {
        body: { email: email.toLowerCase().trim(), phone: verifyPhone, code: otpCode },
      });
      if (error || !data?.success) {
        toast.error(await otpErrorMessage(error, data, 'Invalid code. Please try again.'));
        setAuthLoading(false);
        return;
      }

      if (data.session?.access_token && data.session?.refresh_token) {
        try {
          await supabase.auth.setSession({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          });
        } catch (sessionErr) {
          console.log('Session set warning (non-blocking):', sessionErr);
        }
      }

      localStorage.setItem('bio_subscriber_email', email.toLowerCase().trim());
      localStorage.setItem('bio_account_verified', 'true');
      await loadAccount(email.toLowerCase().trim());
    } catch (e) {
      toast.error('Verification failed');
    } finally {
      setAuthLoading(false);
    }
  };

  // Card management
  const handleDeleteCard = async (pmId: number) => {
    if (!user?.lunarpay_customer_id) return;
    try {
      await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'delete-payment-method', customerId: user.lunarpay_customer_id, paymentMethodId: pmId },
      });
      setSavedCards(prev => prev.filter(c => c.id !== pmId));
      toast.success('Card removed');
    } catch (e) {
      toast.error('Failed to remove card');
    }
  };

  const handleSetDefault = async (pmId: number) => {
    if (!email) return;
    try {
      await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'set-default', email: email.toLowerCase().trim(), paymentMethodId: pmId },
      });
      setSavedCards(prev => prev.map(c => ({ ...c, isDefault: c.id === pmId })));
      toast.success('Default card updated');
    } catch (e) {
      toast.error('Failed to update default');
    }
  };

  const handleAddFunds = async () => {
    const cents = Math.round(parseFloat(fundAmount) * 100);
    if (!cents || cents < 100 || !fundCardId || !user?.lunarpay_customer_id) {
      toast.error('Enter at least $1.00 and select a card');
      return;
    }
    setAddingFunds(true);
    try {
      const { data, error } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'add-funds', email: email.toLowerCase().trim(), amount: cents, paymentMethodId: fundCardId, customerId: user.lunarpay_customer_id },
      });
      if (error || !data?.success) throw new Error(data?.error || 'Failed to add funds');
      setUser(prev => prev ? { ...prev, credits: data.newBalance } : null);
      setFundAmount('');
      toast.success(`$${data.added.toFixed(2)} added to your wallet!`);
    } catch (e: any) {
      const msg = e.message || 'Failed to add funds';
      if (msg.includes('client_customer_id')) toast.error('Payment processing is being updated. Please try again later.');
      else if (msg.includes('decline') || msg.includes('402') || msg.includes('insufficient')) toast.error('Card declined. Please check your balance or try a different card.');
      else toast.error(msg.length > 100 ? 'Card declined or payment failed. Try a different card.' : msg);
    } finally {
      setAddingFunds(false);
    }
  };

  // Add new card flow
  const initAddCard = async () => {
    setAddingCard(true);
    setCardFormReady(false);
    try {
      const { data } = await supabase.functions.invoke('lunarpay-intention', {});
      if (!data?.clientToken) throw new Error('No token');

      const waitForCommerce = () => new Promise<void>((resolve, reject) => {
        if ((window as any).Commerce?.elements) { resolve(); return; }
        if (!document.querySelector('script[src*="commercejs"]')) {
          const script = document.createElement('script');
          script.src = 'https://js.fortis.tech/commercejs-v1.0.0.min.js';
          script.async = true;
          document.body.appendChild(script);
        }
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if ((window as any).Commerce?.elements) { clearInterval(interval); resolve(); }
          if (attempts > 50) { clearInterval(interval); reject(new Error('Failed to load')); }
        }, 100);
      });

      await waitForCommerce();
      const elements = new (window as any).Commerce.elements(data.clientToken);

      elements.eventBus.on('ready', () => setCardFormReady(true));
      elements.eventBus.on('done', async (payload: any) => {
        const ticketId = payload?.data?.id || payload?.id;
        if (!ticketId || !user?.lunarpay_customer_id) return;
        try {
          await supabase.functions.invoke('lunarpay-api', {
            body: { action: 'save-card', customerId: user.lunarpay_customer_id, ticketId, setDefault: false },
          });
          toast.success('Card saved!');
          setAddingCard(false);
          setCommerceInstance(null);
          loadCards(email);
        } catch (e) {
          toast.error('Failed to save card');
        }
      });
      elements.eventBus.on('success', async (payload: any) => {
        const ticketId = payload?.data?.id || payload?.id;
        if (!ticketId || !user?.lunarpay_customer_id) return;
        try {
          await supabase.functions.invoke('lunarpay-api', {
            body: { action: 'save-card', customerId: user.lunarpay_customer_id, ticketId, setDefault: false },
          });
          toast.success('Card saved!');
          setAddingCard(false);
          setCommerceInstance(null);
          loadCards(email);
        } catch (e) {
          toast.error('Failed to save card');
        }
      });

      elements.create({
        container: '#add-card-container',
        theme: 'dark',
        environment: 'production',
        view: 'default',
        language: 'en-us',
        defaultCountry: 'US',
        floatingLabels: true,
        showReceipt: false,
        showSubmitButton: false,
        hideTotal: true,
        appearance: {
          colorBackground: '#111827',
          borderRadius: '8px',
        },
      });

      setCommerceInstance(elements);
    } catch (e) {
      toast.error('Failed to load card form');
      setAddingCard(false);
    }
  };

  // Account update
  const handleSaveAccount = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'update-account', userId: user.id, fullName: editName, phone: editPhone },
      });
      setUser(prev => prev ? { ...prev, full_name: editName, phone: editPhone } : null);
      toast.success('Account updated');
    } catch (e) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-400 bg-green-400/10';
      case 'pending': return 'text-yellow-400 bg-yellow-400/10';
      case 'in_progress': return 'text-blue-400 bg-blue-400/10';
      case 'scheduled': return 'text-purple-400 bg-purple-400/10';
      case 'cancelled': case 'refunded': case 'denied': return 'text-red-400 bg-red-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  const handleDownloadClick = (orderId: string) => {
    setReviewOrderId(orderId);
    setReviewRating(5);
    setReviewComment('');
  };

  const submitReviewAndDownload = async () => {
    const order = orders.find(o => o.id === reviewOrderId);
    if (!order) return;
    setSubmittingReview(true);
    try {
      if (user?.id && order.talent_id) {
        await supabase.from('reviews').insert({
          user_id: user.id,
          talent_id: order.talent_id,
          order_id: order.id,
          rating: reviewRating,
          comment: reviewComment || null,
        });
        toast.success('Thank you for your review!');
      }
    } catch (e) {
      console.log('Review submit error:', e);
    } finally {
      setSubmittingReview(false);
      triggerDownload(order.video_url, order);
      setReviewOrderId(null);
    }
  };

  const skipReviewAndDownload = () => {
    const order = orders.find(o => o.id === reviewOrderId);
    if (order?.video_url) triggerDownload(order.video_url, order);
    setReviewOrderId(null);
  };

  const triggerDownload = async (url: string | null, order?: OrderItem) => {
    if (!url) return;
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isIOS) {
      safeOpen(url);
      toast.success('Video opened! Long-press to save to Photos.', { duration: 6000 });
      return;
    }

    setIsDownloading(true);
    try {
      toast.loading('Preparing download...', { id: 'download-progress' });
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      const response = await fetch(url, { mode: 'cors', signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const talentName = order?.talent_name || 'talent';
      const filename = `shoutout-${talentName.replace(/\s+/g, '-')}-${(order?.id || 'video').slice(0, 8)}.mp4`;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(blobUrl); }, 200);
      toast.success('Video downloaded!', { id: 'download-progress' });
    } catch (error: any) {
      console.error('Blob download error:', error);
      toast.dismiss('download-progress');
      safeOpen(url);
      toast.success('Video opened! Right-click to save.', { duration: 5000 });
    } finally {
      setIsDownloading(false);
    }
  };

  const openDetailsModal = (order: OrderItem) => {
    setDetailsOrderId(order.id);
    setDetailsRecipientName(order.recipient_name || '');
    const mentions = (order.request_details || '').split('\n').filter(Boolean);
    setDetailsMention1(mentions[0] || '');
    setDetailsMention2(mentions[1] || '');
    setShowDetailsModal(true);
  };

  const handleSubmitDetails = async () => {
    if (!detailsRecipientName.trim()) {
      toast.error('Please enter who this video is for');
      return;
    }
    if (!detailsMention1.trim()) {
      toast.error('Please add at least one thing to mention');
      return;
    }
    setSubmittingDetails(true);
    try {
      const { error } = await supabase.rpc('update_order_details', {
        p_order_id: detailsOrderId,
        p_recipient_name: detailsRecipientName.trim(),
        p_request_details: [detailsMention1.trim(), detailsMention2.trim()].filter(Boolean).join('\n'),
      });
      if (error) throw error;
      toast.success('Order details submitted! The talent will start working on your video.');
      setShowDetailsModal(false);
      if (user?.email) loadOrders(user.email, user.id);
    } catch (err) {
      console.error('Error submitting details:', err);
      toast.error('Failed to submit details. Please try again.');
    } finally {
      setSubmittingDetails(false);
    }
  };

  // Auth gate
  if (authStep !== 'authenticated') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a0a] to-[#1a1a2e] flex flex-col items-center justify-center px-4">
        <Toaster position="top-center" />
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img src="/shoutoutwhite.png" alt="ShoutOut" className="h-8 mx-auto mb-4 opacity-60" />
            <h1 className="text-2xl font-bold text-white mb-2">My Account</h1>
            <p className="text-gray-400 text-sm">Sign in to manage your account and view orders.</p>
          </div>

          {authLoading && authStep === 'email' ? (
            <div className="flex flex-col items-center py-8 gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
              <p className="text-gray-400 text-sm">Checking your account...</p>
            </div>
          ) : authStep === 'email' && (
            <div className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleEmailSubmit()}
                placeholder="Enter your email"
                className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40"
              />
              <button
                onClick={handleEmailSubmit}
                disabled={authLoading}
                className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {authLoading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Continue'}
              </button>
            </div>
          )}

          {authStep === 'phone' && (
            <div className="space-y-4">
              <p className="text-gray-300 text-sm text-center">Enter your phone number for verification</p>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(formatPhoneInput(e.target.value))}
                onKeyDown={e => e.key === 'Enter' && handlePhoneSubmit()}
                placeholder="Phone number"
                className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40"
              />
              <p className="text-gray-500 text-xs text-center">{PHONE_HELP_TEXT}</p>
              <button
                onClick={handlePhoneSubmit}
                disabled={authLoading}
                className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {authLoading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Send Code'}
              </button>
              <button onClick={() => setAuthStep('email')} className="w-full text-gray-400 text-sm hover:text-white">Back</button>
            </div>
          )}

          {authStep === 'otp' && (
            <div className="space-y-4">
              {email && <p className="text-white text-sm text-center font-medium">{email}</p>}
              <p className="text-gray-300 text-sm text-center">
                Code sent to {phone ? (phone.includes('*') ? phone : '***' + phone.slice(-4)) : 'your phone'}
              </p>
              <input
                type="text"
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={e => e.key === 'Enter' && handleOtpVerify()}
                placeholder="Enter code"
                className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40 text-center text-2xl tracking-widest"
                maxLength={6}
                autoFocus
              />
              <button
                onClick={handleOtpVerify}
                disabled={authLoading || otpCode.length < 4}
                className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {authLoading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Verify'}
              </button>
              <div className="flex items-center justify-between">
                <button onClick={() => { setAuthStep('email'); setOtpCode(''); setPhone(''); }} className="text-gray-400 text-sm hover:text-white transition-colors">
                  &larr; Back
                </button>
                <button onClick={() => { setAuthStep('phone'); setOtpCode(''); }} className="text-gray-400 text-sm hover:text-white transition-colors">
                  Change phone number
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main account page
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0a] to-[#1a1a2e]">
      <Toaster position="top-center" />

      {/* Header */}
      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white text-sm transition-colors">
            &larr; Back
          </button>
          <img src="/shoutoutwhite.png" alt="ShoutOut" className="h-5 opacity-60" />
          <button
            onClick={openSupportChat}
            className="flex items-center gap-1 text-gray-400 hover:text-white text-sm transition-colors"
          >
            Support
            {supportUnread > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center leading-none">
                {supportUnread}
              </span>
            )}
          </button>
        </div>

        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3 text-2xl">
            {user?.full_name ? user.full_name.charAt(0).toUpperCase() : '?'}
          </div>
          <h1 className="text-xl font-bold text-white">{user?.full_name || user?.email || 'My Account'}</h1>
          <p className="text-gray-400 text-sm">{user?.email}</p>
          <div className="mt-2 inline-flex items-center gap-1.5 bg-green-400/10 text-green-400 px-3 py-1 rounded-full text-sm font-medium">
            <span>$</span>
            <span>{(parseFloat(String(user?.credits || 0))).toFixed(2)}</span>
            <span className="text-green-400/60 text-xs ml-1">credits</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 mb-6">
          {(['orders', 'cards', 'account'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                tab === t ? 'text-white border-b-2 border-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {t === 'orders' ? (orders.some(o => o.service_type === 'donation') ? 'Orders & Subscriptions' : 'Orders') : t === 'cards' ? 'Wallet' : 'Account Info'}
            </button>
          ))}
        </div>

        {/* Orders Tab */}
        {tab === 'orders' && (
          <div className="space-y-3">
            {orders.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-sm">No orders yet</p>
              </div>
            ) : (
              [...orders].sort((a, b) => {
                const aMonthly = a.occasion?.includes('monthly_support') ? -1 : 0;
                const bMonthly = b.occasion?.includes('monthly_support') ? -1 : 0;
                return aMonthly - bMonthly;
              }).map(order => {
                const isExpanded = expandedOrderId === order.id;
                return (
                <div key={order.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  {/* Header — tap to expand */}
                  <button
                    onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                    className="w-full p-4 text-left"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {order.talent_image ? (
                          <img src={order.talent_image} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm text-gray-400">
                            {(order.talent_name || '?').charAt(0)}
                          </div>
                        )}
                        <div>
                          <p className="text-white text-sm font-medium">{order.talent_name || 'Unknown'}</p>
                          <p className="text-gray-400 text-xs">
                            {order.service_type === 'donation' ? (
                              order.occasion === 'monthly_support' ? '💚 Monthly Support' :
                              order.occasion === 'monthly_support_paused' ? '⏸️ Monthly Support (Paused)' :
                              order.occasion === 'monthly_support_cancelled' ? '❌ Monthly Support (Cancelled)' :
                              '💚 Donation'
                            ) : order.occasion || 'ShoutOut'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1">
                        <p className="text-white text-sm font-medium">${(order.amount / 100).toFixed(2)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(order.status)}`}>
                          {order.is_scheduled && order.status === 'scheduled' ? 'Scheduled' : order.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-gray-500 text-xs">{new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                      <div className="flex items-center gap-2">
                        {order.is_scheduled && order.status === 'scheduled' && order.scheduled_charge_date && (
                          <span className="text-purple-400 text-xs">Charges {new Date(order.scheduled_charge_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        )}
                        {order.is_express_delivery && <span className="text-yellow-400 text-xs">Express</span>}
                        {order.video_url && <span className="text-green-400 text-xs">Video Ready</span>}
                        <span className="text-gray-500 text-xs">{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </div>
                  </button>

                  {/* Fill in details prompt — show for scheduled orders too */}
                  {!order.details_submitted && (order.status === 'pending' || order.status === 'scheduled') && order.service_type !== 'donation' && order.service_type !== 'credit_purchase' && (
                    <div className="px-4 pb-3">
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-amber-400 text-sm font-medium">Details Required</p>
                            <p className="text-gray-400 text-xs mt-0.5">Tell the talent who this is for and what to say</p>
                          </div>
                          <button
                            onClick={() => openDetailsModal(order)}
                            className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap"
                          >
                            Fill in Details
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-white/5">
                      {/* Media delivery */}
                      {order.video_url && (
                        <div className="mt-3 rounded-xl overflow-hidden bg-black">
                          {order.is_audio_shoutout ? (
                            <div className="p-4">
                              <div className="flex items-center gap-3 mb-3">
                                <span className="text-2xl">🎙️</span>
                                <span className="text-white text-sm font-medium">Audio ShoutOut</span>
                              </div>
                              <audio src={order.video_url} controls className="w-full" />
                            </div>
                          ) : (
                            <video src={order.video_url} controls playsInline className="w-full max-h-[400px] object-contain" poster={order.talent_image || undefined} />
                          )}
                        </div>
                      )}

                      {/* Order details */}
                      <div className="mt-3 space-y-2">
                        {order.recipient_name && (
                          <div className="flex justify-between">
                            <span className="text-gray-500 text-xs">For</span>
                            <span className="text-white text-xs">{order.recipient_name}</span>
                          </div>
                        )}
                        {order.occasion && (
                          <div className="flex justify-between">
                            <span className="text-gray-500 text-xs">Occasion</span>
                            <span className="text-white text-xs capitalize">{order.occasion}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-500 text-xs">Amount</span>
                          <span className="text-white text-xs">${(order.amount / 100).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 text-xs">Status</span>
                          <span className={`text-xs capitalize ${order.status === 'completed' ? 'text-green-400' : order.status === 'pending' ? 'text-yellow-400' : order.status === 'scheduled' ? 'text-purple-400' : 'text-white'}`}>
                            {order.is_scheduled && order.status === 'scheduled' ? 'Scheduled' : order.status.replace('_', ' ')}
                          </span>
                        </div>
                        {order.is_express_delivery && (
                          <div className="flex justify-between">
                            <span className="text-gray-500 text-xs">Delivery</span>
                            <span className="text-yellow-400 text-xs">Express (24hr)</span>
                          </div>
                        )}
                        {order.request_details && (
                          <div className="mt-2 pt-2 border-t border-white/5">
                            <p className="text-gray-500 text-xs mb-1">Request Details</p>
                            <p className="text-gray-300 text-xs">{order.request_details}</p>
                          </div>
                        )}
                      </div>

                      {/* Subscription management for monthly support */}
                      {order.occasion === 'monthly_support' && order.service_type === 'donation' && (
                        <div className="mt-3 pt-3 border-t border-white/5">
                          <p className="text-green-400 text-xs font-medium mb-3">Active Monthly Support</p>

                          {/* Edit amount */}
                          {editingSubOrderId === order.id ? (
                            <div className="flex gap-2 mb-3">
                              <div className="flex-1 relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                <input type="number" value={editingSubAmount} onChange={e => setEditingSubAmount(e.target.value)} min="1" className="w-full bg-white/5 border border-white/20 pl-7 pr-3 py-2 rounded-lg text-white text-sm focus:outline-none" placeholder="New amount" />
                              </div>
                              <p className="text-gray-500 text-[10px] mt-1">+5% processing fee applied</p>
                              <button
                                onClick={async () => {
                                  const newAmt = parseFloat(editingSubAmount);
                                  if (!newAmt || newAmt < 1) { toast.error('Min $1'); return; }
                                  setSubActionLoading(order.id);
                                  const fee = Math.round(newAmt * 0.05 * 100) / 100;
                                  const totalCents = Math.round((newAmt + fee) * 100);
                                  try {
                                    // Update order amount
                                    await supabase.functions.invoke('lunarpay-api', {
                                      body: { action: 'update-order', orderId: order.id, updates: { amount: totalCents, admin_fee: Math.round(newAmt * 0.075 * 100) } },
                                    });
                                    // Update LunarPay subscription if customer has one
                                    if (user?.lunarpay_customer_id) {
                                      try {
                                        const { data: subs } = await supabase.functions.invoke('lunarpay-api', {
                                          body: { action: 'get-subscriptions', customerId: user.lunarpay_customer_id },
                                        });
                                        if (subs?.subscriptions?.length) {
                                          await supabase.functions.invoke('lunarpay-api', {
                                            body: { action: 'update-subscription', subscriptionId: subs.subscriptions[0].id, amount: totalCents },
                                          });
                                        }
                                      } catch {}
                                    }
                                    setOrders(prev => prev.map(o => o.id === order.id ? { ...o, amount: totalCents } : o));
                                    toast.success(`Updated to $${newAmt.toFixed(2)} + $${fee.toFixed(2)} fee`);
                                    setEditingSubOrderId(null);
                                  } catch { toast.error('Failed to update'); }
                                  finally { setSubActionLoading(null); }
                                }}
                                disabled={subActionLoading === order.id}
                                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                              >Save</button>
                              <button onClick={() => setEditingSubOrderId(null)} className="px-3 py-2 text-gray-400 text-sm">Cancel</button>
                            </div>
                          ) : (
                            <div className="flex gap-2 mb-3">
                              <button
                                onClick={() => { setEditingSubOrderId(order.id); setEditingSubAmount(((order.amount || 0) / 100).toFixed(0)); }}
                                className="flex-1 py-2 text-center text-sm font-medium text-blue-400 bg-blue-400/10 rounded-lg hover:bg-blue-400/20 transition-colors"
                              >Edit Amount</button>
                              <button
                                onClick={async () => {
                                  setSubActionLoading(order.id);
                                  try {
                                    await supabase.functions.invoke('lunarpay-api', {
                                      body: { action: 'update-order', orderId: order.id, updates: { status: 'paused', occasion: 'monthly_support_paused' } },
                                    });
                                    // Pause in LunarPay
                                    if (user?.lunarpay_customer_id) {
                                      try {
                                        const { data: subs } = await supabase.functions.invoke('lunarpay-api', { body: { action: 'get-subscriptions', customerId: user.lunarpay_customer_id } });
                                        if (subs?.subscriptions?.length) await supabase.functions.invoke('lunarpay-api', { body: { action: 'cancel-subscription', subscriptionId: subs.subscriptions[0].id } });
                                      } catch {}
                                    }
                                    setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'paused', occasion: 'monthly_support_paused' } : o));
                                    toast.success('Support paused');
                                  } catch { toast.error('Failed'); }
                                  finally { setSubActionLoading(null); }
                                }}
                                disabled={subActionLoading === order.id}
                                className="flex-1 py-2 text-center text-sm font-medium text-yellow-400 bg-yellow-400/10 rounded-lg hover:bg-yellow-400/20 transition-colors"
                              >Pause</button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm('Cancel your monthly support? You will lose your 10% ShoutOut discount.')) return;
                                  setSubActionLoading(order.id);
                                  try {
                                    await supabase.functions.invoke('lunarpay-api', {
                                      body: { action: 'update-order', orderId: order.id, updates: { status: 'cancelled', occasion: 'monthly_support_cancelled' } },
                                    });
                                    // Cancel in LunarPay
                                    if (user?.lunarpay_customer_id) {
                                      try {
                                        const { data: subs } = await supabase.functions.invoke('lunarpay-api', { body: { action: 'get-subscriptions', customerId: user.lunarpay_customer_id } });
                                        if (subs?.subscriptions?.length) await supabase.functions.invoke('lunarpay-api', { body: { action: 'cancel-subscription', subscriptionId: subs.subscriptions[0].id } });
                                      } catch {}
                                    }
                                    setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'cancelled', occasion: 'monthly_support_cancelled' } : o));
                                    toast.success('Monthly support cancelled');
                                  } catch { toast.error('Failed'); }
                                  finally { setSubActionLoading(null); }
                                }}
                                disabled={subActionLoading === order.id}
                                className="flex-1 py-2 text-center text-sm font-medium text-red-400 bg-red-400/10 rounded-lg hover:bg-red-400/20 transition-colors"
                              >Cancel</button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Paused subscription - resume button */}
                      {order.occasion === 'monthly_support_paused' && (
                        <div className="mt-3 pt-3 border-t border-white/5">
                          <p className="text-yellow-400 text-xs font-medium mb-2">Paused</p>
                          <button
                            onClick={async () => {
                              setSubActionLoading(order.id);
                              try {
                                // Charge immediately
                                const defaultCard = savedCards.find(c => c.isDefault) || savedCards[0];
                                if (defaultCard && user?.lunarpay_customer_id) {
                                  await supabase.functions.invoke('lunarpay-api', {
                                    body: { action: 'charge', customerId: user.lunarpay_customer_id, paymentMethodId: defaultCard.id, amount: order.amount, description: 'Monthly Support (resumed)' },
                                  });
                                  // Recreate subscription in LunarPay
                                  await supabase.functions.invoke('lunarpay-api', {
                                    body: { action: 'create-subscription', customerId: user.lunarpay_customer_id, paymentMethodId: defaultCard.id, amount: order.amount, frequency: 'monthly' },
                                  });
                                }
                                await supabase.functions.invoke('lunarpay-api', {
                                  body: { action: 'update-order', orderId: order.id, updates: { status: 'completed', occasion: 'monthly_support' } },
                                });
                                setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'completed', occasion: 'monthly_support' } : o));
                                toast.success('Support resumed! You were charged immediately.');
                              } catch (e: any) { toast.error(e.message || 'Failed to resume'); }
                              finally { setSubActionLoading(null); }
                            }}
                            disabled={subActionLoading === order.id}
                            className="w-full py-2.5 text-center text-sm font-medium text-green-400 bg-green-400/10 rounded-lg hover:bg-green-400/20 transition-colors"
                          >Resume Support</button>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="mt-3 pt-3 border-t border-white/5 flex gap-3">
                        {order.video_url && (
                          <button
                            onClick={() => handleDownloadClick(order.id)}
                            disabled={isDownloading}
                            className="flex-1 py-2.5 text-center text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {isDownloading ? (
                              <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Downloading...</>
                            ) : 'Download Video'}
                          </button>
                        )}
                        {order.talent_slug && (
                          <a
                            href={`/${order.talent_slug}`}
                            className="flex-1 py-2.5 text-center text-sm font-medium text-gray-400 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                          >
                            Visit {order.talent_name}
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                );
              })
            )}
          </div>
        )}

        {/* Wallet Tab */}
        {tab === 'cards' && (
          <div className="space-y-4">
            {/* Balance + Add Funds */}
            <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-white/10 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Wallet Balance</p>
                  <p className="text-3xl font-bold text-white">${(parseFloat(String(user?.credits || 0))).toFixed(2)}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-2xl">💰</div>
              </div>

              {/* Quick add amounts */}
              <div className="flex gap-2 mb-3">
                {[20, 50, 250, 500].map(amt => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setFundAmount(String(amt))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${fundAmount === String(amt) ? 'bg-white/20 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                  >
                    ${amt}
                  </button>
                ))}
              </div>

              {/* Custom amount */}
              <div className="flex gap-2 mb-3">
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={fundAmount}
                    onChange={e => setFundAmount(e.target.value)}
                    placeholder="0.00"
                    min="1"
                    step="0.01"
                    className="w-full bg-white/5 border border-white/20 pl-7 pr-3 py-2.5 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-white/40 text-sm"
                  />
                </div>
              </div>

              {/* Card selector for funding */}
              {savedCards.length > 0 && (
                <select
                  value={fundCardId || ''}
                  onChange={e => setFundCardId(Number(e.target.value))}
                  className="w-full bg-white/5 border border-white/20 px-3 py-2.5 rounded-lg text-white text-sm focus:outline-none focus:border-white/40 mb-3 appearance-none"
                >
                  <option value="" className="bg-gray-800">Select a card</option>
                  {savedCards.map((c: any) => (
                    <option key={c.id} value={c.id} className="bg-gray-800">
                      {(c.sourceType || 'Card').toUpperCase()} ****{c.lastDigits}{c.isDefault ? ' (Default)' : ''}
                    </option>
                  ))}
                </select>
              )}

              <button
                type="button"
                onClick={handleAddFunds}
                disabled={addingFunds || !fundAmount || parseFloat(fundAmount) < 1 || !fundCardId}
                className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
              >
                {addingFunds ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : `Add $${fundAmount || '0.00'} to Wallet`}
              </button>

              <p className="text-gray-500 text-xs mt-3 text-center">
                ShoutOut Credits are non-refundable credits that can be spent on any creator using ShoutOut Fans.
              </p>
            </div>

            {/* Saved Cards */}
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Payment Methods</p>
              {savedCards.map(card => (
                <div key={card.id} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💳</span>
                    <div>
                      <p className="text-white text-sm font-medium">{(card.sourceType || 'Card').toUpperCase()} ****{card.lastDigits || '····'}</p>
                      {card.expMonth && <p className="text-gray-400 text-xs">Exp {card.expMonth}/{card.expYear}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {card.isDefault ? (
                      <span className="text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded">Default</span>
                    ) : (
                      <button onClick={() => handleSetDefault(card.id)} className="text-xs text-blue-400 hover:text-blue-300">Set Default</button>
                    )}
                    <button onClick={() => handleDeleteCard(card.id)} className="text-red-400 text-xs hover:text-red-300">Remove</button>
                  </div>
                </div>
              ))}

              {addingCard ? (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 overflow-hidden">
                  <p className="text-white text-sm font-medium mb-2">Add New Card</p>
                  {!cardFormReady && (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-400" />
                    </div>
                  )}
                  <style>{`#add-card-container button[type="submit"], #add-card-container .submit-button, #add-card-container [class*="SubmitButton"], #add-card-container > div > button { display: none !important; }`}</style>
                  <div id="add-card-container" ref={cardContainerRef} className="min-h-[320px]" style={{ marginTop: '-70px' }} />
                  {cardFormReady && (
                    <button type="button" onClick={() => { if (cardInstance) cardInstance.submit(); }} className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors mt-2">Save Card</button>
                  )}
                  <button onClick={() => { setAddingCard(false); setCommerceInstance(null); }} className="w-full text-gray-400 text-sm mt-3 hover:text-white">Cancel</button>
                </div>
              ) : (
                <button onClick={initAddCard} className="w-full py-3 rounded-xl font-semibold text-white bg-white/10 hover:bg-white/15 border border-white/10 transition-colors">+ Add New Card</button>
              )}
            </div>
          </div>
        )}

        {/* Account Info Tab */}
        {tab === 'account' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email</label>
              <div className="w-full bg-white/5 border border-white/10 px-4 py-3 rounded-xl text-gray-400 text-sm">
                {user?.email}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Full Name</label>
              <input
                type="text"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Phone</label>
              <input
                type="tel"
                value={editPhone}
                onChange={e => setEditPhone(e.target.value)}
                className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40"
                placeholder="Phone number"
              />
            </div>
            <LeaderboardNameSetting userId={user?.id} />
            <div>
              <label className="block text-sm text-gray-400 mb-1">Member Since</label>
              <div className="w-full bg-white/5 border border-white/10 px-4 py-3 rounded-xl text-gray-400 text-sm">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}
              </div>
            </div>
            <button
              onClick={handleSaveAccount}
              disabled={saving}
              className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Save Changes'}
            </button>
            <button
              onClick={() => { setAuthStep('email'); setUser(null); localStorage.removeItem('bio_subscriber_email'); localStorage.removeItem('bio_account_verified'); }}
              className="w-full py-3 rounded-xl font-medium text-gray-400 border border-white/10 hover:bg-white/5 transition-colors mt-3"
            >
              Sign Out
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="pt-8 pb-4 text-center">
          <p className="text-gray-600 text-xs">Powered by ShoutOut</p>
        </div>
      </div>

      {/* Fill in Details Modal */}
      {showDetailsModal && detailsOrderId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/80"
            onClick={() => !submittingDetails && setShowDetailsModal(false)}
          />
          <div className="relative bg-[#1a1a2e] rounded-2xl w-full max-w-sm p-6 z-10 border border-white/10">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white">Fill in Order Details</h3>
              <p className="text-sm text-gray-400 mt-1">Tell the talent what you want in your ShoutOut</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Who is this video for? <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={detailsRecipientName}
                  onChange={(e) => setDetailsRecipientName(e.target.value)}
                  placeholder="Enter the recipient's name"
                  className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  What would you like them to mention? <span className="text-red-400">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">Add 1-2 things you'd like included</p>
                <div className="space-y-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">1.</span>
                    <input
                      type="text"
                      value={detailsMention1}
                      onChange={(e) => setDetailsMention1(e.target.value.slice(0, 160))}
                      placeholder="e.g., Wish them a happy birthday"
                      className="w-full pl-8 pr-16 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      maxLength={160}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">{detailsMention1.length}/160</span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">2.</span>
                    <input
                      type="text"
                      value={detailsMention2}
                      onChange={(e) => setDetailsMention2(e.target.value.slice(0, 160))}
                      placeholder="e.g., They're a huge fan (optional)"
                      className="w-full pl-8 pr-16 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      maxLength={160}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">{detailsMention2.length}/160</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowDetailsModal(false)}
                disabled={submittingDetails}
                className="flex-1 px-4 py-2.5 bg-white/5 border border-white/20 rounded-lg text-white text-sm hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitDetails}
                disabled={submittingDetails}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {submittingDetails ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : 'Submit Details'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewOrderId && (() => {
        const order = orders.find(o => o.id === reviewOrderId);
        if (!order) return null;
        return (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => skipReviewAndDownload()}>
            <div className="bg-[#1a1a2e] rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
              <div className="text-center mb-5">
                {order.talent_image && (
                  <img src={order.talent_image} alt="" className="w-14 h-14 rounded-full mx-auto mb-3 object-cover" />
                )}
                <h3 className="text-white text-lg font-bold">How was your ShoutOut?</h3>
                <p className="text-gray-400 text-sm mt-1">Leave a review for {order.talent_name}</p>
              </div>

              {/* Star rating */}
              <div className="flex justify-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReviewRating(star); }}
                    className="w-10 h-10 flex items-center justify-center text-3xl transition-transform hover:scale-110 cursor-pointer select-none"
                    style={{ color: star <= reviewRating ? '#facc15' : '#4b5563' }}
                  >
                    ★
                  </button>
                ))}
              </div>

              {/* Comment */}
              <textarea
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                placeholder="Tell others about your experience (optional)"
                className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-white/40 resize-none"
                rows={3}
              />

              {/* Buttons */}
              <div className="mt-4 space-y-2">
                <button
                  onClick={submitReviewAndDownload}
                  disabled={submittingReview}
                  className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submittingReview ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    'Submit Review & Download'
                  )}
                </button>
                <button
                  onClick={skipReviewAndDownload}
                  className="w-full py-2.5 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Skip & Download
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Support Chat Panel */}
      {showSupportChat && (
        <div className="fixed inset-0 z-50 flex flex-col">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowSupportChat(false)} />
          <div className="relative mt-auto bg-[#0f172a] border-t border-white/10 rounded-t-2xl max-h-[80vh] flex flex-col z-10" style={{ minHeight: '60vh' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h3 className="text-white font-semibold text-base">{isCreator ? 'Message ShoutOut' : 'Support Chat'}</h3>
              <button onClick={() => setShowSupportChat(false)} className="text-gray-400 hover:text-white p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {creatorCheckFailed && (
              <div className="px-5 py-3 border-b border-white/10 text-sm text-gray-300">
                <p>
                  We couldn&apos;t check your account.{' '}
                  <button type="button" onClick={() => setCreatorCheckRun((n) => n + 1)} className="text-blue-400 underline">
                    Try again
                  </button>
                </p>
              </div>
            )}
            {isCreator && (
              <div className="px-5 py-3 border-b border-white/10 text-sm text-gray-300 space-y-1">
                {creatorNeedsDashboard ? (
                  <p>
                    Open your creator dashboard to message the team:{' '}
                    <a href={CREATOR_INBOX_URL} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">
                      Creator Inbox
                    </a>
                    .
                  </p>
                ) : creatorSupportSent ? (
                  <p>
                    Sent. A real person will reply next business day. The reply will be in your{' '}
                    <a href={CREATOR_INBOX_URL} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">
                      Creator Inbox
                    </a>
                    , not here.
                  </p>
                ) : (
                  <p>
                    Your message goes to the ShoutOut team. A real person will reply next business day, in your{' '}
                    <a href={CREATOR_INBOX_URL} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">
                      Creator Inbox
                    </a>
                    .
                  </p>
                )}
                {supportMessages.length > 0 && (
                  <p className="text-xs text-gray-500 pt-1">Below: your earlier Help Desk messages.</p>
                )}
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {loadingSupport ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" />
                </div>
              ) : supportMessages.length > 0 ? (
                supportMessages.map((msg) => (
                  <div key={msg.id} className="space-y-2">
                    {msg.message.startsWith('Admin:') || msg.message === '[Admin initiated conversation]' ? (
                      <div className="flex justify-start">
                        <div className="bg-white/10 rounded-xl px-3 py-2 max-w-[80%] border-l-2 border-blue-500">
                          <p className="text-blue-400 text-[10px] font-medium mb-0.5">Admin</p>
                          <p className="text-white text-sm">{msg.message.startsWith('Admin:') ? msg.message.replace('Admin:', '').trim() : msg.response || ''}</p>
                          <p className="text-gray-500 text-[10px] mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-end">
                          <div className="bg-blue-600 rounded-xl px-3 py-2 max-w-[80%]">
                            <p className="text-white text-sm">{msg.message}</p>
                            <p className="text-blue-200 text-[10px] mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                        {msg.response && (
                          <div className="flex justify-start">
                            <div className="bg-white/10 rounded-xl px-3 py-2 max-w-[80%] border-l-2 border-blue-500">
                              <p className="text-blue-400 text-[10px] font-medium mb-0.5">Admin</p>
                              <p className="text-white text-sm">{msg.response}</p>
                              <p className="text-gray-500 text-[10px] mt-1">{msg.updated_at ? new Date(msg.updated_at).toLocaleString() : ''}</p>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))
              ) : (
                isCreator ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-gray-400 text-sm">Your messages and the team&apos;s replies are in your Creator Inbox.</p>
                  </div>
                ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="text-3xl mb-2">💬</div>
                  <p className="text-gray-400 text-sm">No messages yet</p>
                  <p className="text-gray-500 text-xs mt-1">Send a message to get help!</p>
                </div>
                )
              )}
              <div ref={supportEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-white/10 px-4 py-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSupportMsg}
                  onChange={(e) => setNewSupportMsg(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendSupportMessage(); } }}
                  placeholder="Type your message..."
                  className="flex-1 bg-white/5 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-white/40"
                  maxLength={500}
                />
                <button
                  onClick={sendSupportMessage}
                  disabled={!newSupportMsg.trim() || sendingSupport || isCreator === null}
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-40 flex-shrink-0"
                >
                  {sendingSupport ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-gray-600 text-[10px] mt-1">{newSupportMsg.length}/500</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;
